import type { JacintaMemoryEntry } from '@/modules/shared/types/yc-demo';

const TEAM = {
  justin: { name: 'Justin Tang', avatarUrl: '/avatars/justin.png', role: 'Hiring Manager — GTM' },
  thomas: { name: 'Thomas Wong', avatarUrl: '/avatars/thomas.png', role: 'Co-founder' },
  maya: { name: 'Maya Chen', avatarUrl: '/avatars/maya.png', role: 'Recruiting Lead' },
  avery: { name: 'Avery Patel', avatarUrl: '/avatars/avery.png', role: 'People Ops' },
  sam: { name: 'Sam Lee', avatarUrl: '/avatars/sam.png', role: 'Engineering Manager' },
  devi: { name: 'Devi Krishnan', avatarUrl: '/avatars/devi.png', role: 'Hiring Manager — Eng' },
  kenji: { name: 'Kenji Sato', avatarUrl: '/avatars/kenji.png', role: 'Head of Talent' },
} as const;

export const seedMemories: JacintaMemoryEntry[] = [
  // ─── Last 7 days (very low applied counts) ──────────────────────────
  {
    id: 'mem-001',
    createdAt: '2026-04-29T14:22:11Z',
    category: 'scoring',
    attributedTo: TEAM.justin,
    trigger: 'When scoring AE candidates for cultural fit at <50-person SaaS companies',
    insight:
      'Justin scored Maya Chen 4 vs my 3 on the Crypto.com AE shortlist — he weights "comfort with ambiguity" much higher than "polished discovery process." After 4 hires from this calibration, applying to all early-stage AE roles tagged Series A or earlier.',
    appliesTo: ['cv_screening', 'ai_interview'],
    appliedCount: 2,
    source: 'score_edit',
    relatedJobIds: [],
  },
  {
    id: 'mem-002',
    createdAt: '2026-04-28T09:45:00Z',
    category: 'scoring',
    attributedTo: TEAM.sam,
    trigger: 'When scoring Senior Backend Engineers without distributed-systems production experience',
    insight:
      'Sam down-scored 2 candidates I rated 4 with note "Rails-monolith experience does not translate to our Postgres-replica + queues setup at our scale." Now flagging "no production sharding / queue ownership" as a -1 modifier on Senior+ backend roles.',
    appliesTo: ['cv_screening', 'ai_interview'],
    appliedCount: 0,
    source: 'score_edit',
    relatedJobIds: [],
  },
  {
    id: 'mem-003',
    createdAt: '2026-04-27T11:08:33Z',
    category: 'pipeline',
    attributedTo: TEAM.maya,
    trigger: 'For Founding AE roles at Series A SaaS companies',
    insight:
      'Pattern from 3 successful hires (Crypto.com, Sino Group, Hyatt): replace the standard "case study" stage with a 90-min "deal review" pairing the candidate with the CEO. Hires from the new loop ramped 38% faster vs the case-study cohort.',
    appliesTo: ['pipeline'],
    appliedCount: 1,
    source: 'pipeline_outcome',
    relatedJobIds: [],
  },
  {
    id: 'mem-004',
    createdAt: '2026-04-26T16:30:00Z',
    category: 'sourcing',
    attributedTo: TEAM.thomas,
    trigger: 'When sourcing Staff+ ML candidates for AI infra roles',
    insight:
      'Thomas observed our 3 strongest Staff ML hires came from Cohere, Anyscale, and Modal — not FAANG. Reweighting source priorities: ICs from infra-native companies +30%, FAANG-only profiles +0%. Their stack assumptions match ours.',
    appliesTo: ['sourcing', 'cv_screening'],
    appliedCount: 3,
    source: 'observed',
    relatedJobIds: [],
  },

  // ─── 7–30 days old (low applied counts) ─────────────────────────────
  {
    id: 'mem-005',
    createdAt: '2026-04-25T13:15:00Z',
    category: 'scoring',
    attributedTo: TEAM.avery,
    trigger: 'When evaluating tenure red flags (>3 jobs in 18 months) for IC roles',
    insight:
      'Avery flagged the auto-deduction as too aggressive — 4 of our last 6 strong hires had 3 short stints due to acquisitions or pivots, not performance. Switched to "investigate via reference call" instead of auto-deduct. False-rejection rate on tenure flags dropped 22%.',
    appliesTo: ['cv_screening'],
    appliedCount: 5,
    source: 'observed',
    relatedJobIds: [],
  },
  {
    id: 'mem-006',
    createdAt: '2026-04-23T10:00:00Z',
    category: 'scoring',
    attributedTo: TEAM.justin,
    trigger: 'When scoring Marketing leads for content-led growth backgrounds',
    insight:
      'Justin re-scored 2 candidates 5→4 with note "pure SEO operators do not ship our scrappy demand-gen motion fast enough at <$5M ARR." Now requiring evidence of paid + organic + lifecycle blended ownership for early-stage marketing hires.',
    appliesTo: ['cv_screening', 'ai_interview'],
    appliedCount: 6,
    source: 'score_edit',
    relatedJobIds: [],
  },
  {
    id: 'mem-007',
    createdAt: '2026-04-21T15:42:00Z',
    category: 'sourcing',
    attributedTo: TEAM.maya,
    trigger: 'When sourcing PMs for B2B products where engineers are >50% of users',
    insight:
      'Maya flagged 3 strong-on-paper PM candidates as misfit — none had shipped developer-facing products. Adjusting screening: require "PMd APIs / SDKs / dev tools or developer-experience surfaces" for any product where engineers are the primary buyer.',
    appliesTo: ['sourcing', 'cv_screening'],
    appliedCount: 7,
    source: 'score_edit',
    relatedJobIds: [],
  },
  {
    id: 'mem-008',
    createdAt: '2026-04-20T08:30:00Z',
    category: 'pipeline',
    attributedTo: TEAM.devi,
    trigger: 'For Senior Frontend Engineer pipelines (React + Next.js, sub-30 candidate volume)',
    insight:
      '4 successful hires shared one signal — strong on the "rebuild this 200-line component" live exercise but mediocre on the take-home. Take-home is now optional / trust-building; the live debug pair-prog stage is decision-making. Cut time-to-offer by 6 days.',
    appliesTo: ['pipeline'],
    appliedCount: 4,
    source: 'pipeline_outcome',
    relatedJobIds: [],
  },
  {
    id: 'mem-009',
    createdAt: '2026-04-18T12:11:00Z',
    category: 'culture',
    attributedTo: TEAM.thomas,
    trigger: 'When detecting "Founder Fit" risk on first-leadership-hire candidates',
    insight:
      'Thomas surfaced a pattern: 2 Heads-of-X roles (Sales, Marketing) churned within 8 months when the candidate\'s prior leader gave them more autonomy than Thomas comfortably grants in year one. Now actively probing "preferred autonomy ramp" in final-round.',
    appliesTo: ['ai_interview'],
    appliedCount: 8,
    source: 'observed',
    relatedJobIds: [],
  },
  {
    id: 'mem-010',
    createdAt: '2026-04-15T14:00:00Z',
    category: 'scoring',
    attributedTo: TEAM.kenji,
    trigger: 'When scoring APAC-based candidates for US-headquartered Enterprise Sales roles',
    insight:
      'Kenji re-scored 3 APAC candidates citing missing MEDDPICC + "buying committee in EST timezone" pattern recognition. Adjusted: only apply -1 if explicit MEDDPICC + multi-TZ exec-engagement examples are absent in the CV — not by default for region.',
    appliesTo: ['cv_screening', 'ai_interview'],
    appliedCount: 11,
    source: 'score_edit',
    relatedJobIds: [],
  },
  {
    id: 'mem-011',
    createdAt: '2026-04-12T09:20:00Z',
    category: 'compensation',
    attributedTo: TEAM.avery,
    trigger: 'When proposing comp for APAC-based Senior Frontend Engineers',
    insight:
      'Avery noted our $160K–$200K USD bands map to S$200K–S$240K total in Singapore (after benefits + equity rounding) — the perceived offer is 18% lower than the US-equivalent. Adding +$15K base for SG-based candidates. Applied to 4 offers, all accepted.',
    appliesTo: ['cv_screening', 'pipeline'],
    appliedCount: 10,
    source: 'observed',
    relatedJobIds: [],
  },
  {
    id: 'mem-012',
    createdAt: '2026-04-10T16:45:00Z',
    category: 'scoring',
    attributedTo: TEAM.sam,
    trigger: 'When scoring backend candidates from monorepo / Bazel / Go-first stacks',
    insight:
      'Sam scored 2 candidates 5→3 noting their stack was Rails-monorepo, not Bazel-Go. I reverted his edits — our infra is moving toward Go-monorepo in Q3, so Bazel-Go fluency is a +1 modifier, not a hard requirement, and Rails-monorepo experience still maps cleanly.',
    appliesTo: ['cv_screening'],
    appliedCount: 9,
    source: 'score_edit',
    relatedJobIds: [],
  },
  {
    id: 'mem-013',
    createdAt: '2026-04-08T11:00:00Z',
    category: 'scoring',
    attributedTo: TEAM.justin,
    trigger: 'When evaluating SDR-promoted-to-AE candidates against the "owns full sales cycle" rubric',
    insight:
      'Justin flagged 2 SDR-promoted candidates as overrated by my screen. Pattern: SDRs promoted internally often lack discovery-call mastery vs AEs hired externally. Now requiring 18+ months as quota-carrying AE (not SDR or BDR) for Mid+ AE roles.',
    appliesTo: ['cv_screening', 'ai_interview'],
    appliedCount: 13,
    source: 'score_edit',
    relatedJobIds: [],
  },
  {
    id: 'mem-014',
    createdAt: '2026-04-07T10:30:00Z',
    category: 'sourcing',
    attributedTo: TEAM.thomas,
    trigger: 'When sourcing for Senior ML Engineer roles',
    insight:
      'Thomas added "must have ≥1 paper at NeurIPS / ICML / ICLR in the last 3 years" after 2 strong-on-paper hires couldn\'t keep up with the team\'s research-velocity. Now requiring this signal on all Senior ML+ roles.',
    appliesTo: ['cv_screening', 'sourcing'],
    appliedCount: 12,
    source: 'manual',
    relatedJobIds: [],
  },
  {
    id: 'mem-015',
    createdAt: '2026-04-05T13:30:00Z',
    category: 'pipeline',
    attributedTo: TEAM.maya,
    trigger: 'For the first 3 hires of any new pipeline at <Pre-Seed stage',
    insight:
      'Pattern: pre-seed founders hit interview-fatigue at hire #3. Compressing to a 3-stage pipeline (intro → pair-work → closing) for early roles, then expanding to 5-stage from hire #4 onward. Tested at 2 portfolio cos — hire-to-offer time dropped 28%.',
    appliesTo: ['pipeline'],
    appliedCount: 15,
    source: 'pipeline_outcome',
    relatedJobIds: [],
  },

  // ─── 30–60 days old (medium applied counts) ─────────────────────────
  {
    id: 'mem-016',
    createdAt: '2026-04-03T09:15:00Z',
    category: 'scoring',
    attributedTo: TEAM.devi,
    trigger: 'When evaluating "polyglot vs specialist" trade-offs for Senior Backend hires',
    insight:
      'Devi re-scored 4 candidates citing "we need depth in Postgres + Go, not breadth across 5 stacks." Adjusting screening to favor T-shaped specialists over polyglot generalists for the Eng team\'s current phase. Will re-evaluate after Series B.',
    appliesTo: ['cv_screening', 'ai_interview'],
    appliedCount: 16,
    source: 'score_edit',
    relatedJobIds: [],
  },
  {
    id: 'mem-017',
    createdAt: '2026-04-01T14:50:00Z',
    category: 'culture',
    attributedTo: TEAM.maya,
    trigger: 'When evaluating remote / async-first fit for Senior IC roles',
    insight:
      'Maya tracked 2 of 3 hires who failed culture-fit cited "too many sync meetings" in exit interviews. Now explicitly screening for "thrives in async, written-first culture" with concrete examples — NOT proxy questions about WFH preferences.',
    appliesTo: ['ai_interview'],
    appliedCount: 14,
    source: 'observed',
    relatedJobIds: [],
  },
  {
    id: 'mem-018',
    createdAt: '2026-03-30T08:00:00Z',
    category: 'scoring',
    attributedTo: TEAM.justin,
    trigger: 'When scoring AE candidates from "logo-heavy" enterprise backgrounds (Salesforce, Oracle)',
    insight:
      'Justin scored Maya 4 vs my 3 on a Salesforce-Oracle veteran candidate, noting brand recognition correlates 0.2 with our SMB performance. Recalibrating: enterprise prestige +0; mid-market grit (Toast, Justworks, Ramp) +1 for our SMB segment.',
    appliesTo: ['cv_screening', 'ai_interview'],
    appliedCount: 22,
    source: 'score_edit',
    relatedJobIds: [],
  },
  {
    id: 'mem-019',
    createdAt: '2026-03-28T10:20:00Z',
    category: 'pipeline',
    attributedTo: TEAM.sam,
    trigger: 'For Senior Engineering hires at Series A and beyond',
    insight:
      '5 of 6 strong hires came through the "system design + production debug" combo stage. Cutting algorithmic LeetCode entirely — high false-negative rate on senior+ ICs who haven\'t drilled in 5+ years. Replaced with a 90-min real-system architecture review.',
    appliesTo: ['pipeline'],
    appliedCount: 19,
    source: 'pipeline_outcome',
    relatedJobIds: [],
  },
  {
    id: 'mem-020',
    createdAt: '2026-03-26T11:30:00Z',
    category: 'sourcing',
    attributedTo: TEAM.kenji,
    trigger: 'When sourcing for Senior+ roles in the Australia / NZ markets',
    insight:
      'Kenji noted LinkedIn alone misses ~40% of senior local talent who live on Seek + word-of-mouth. Adding a Seek-specific sourcing pass and "intro via portfolio company alumni" outreach for AU / NZ roles. Yielded 9 strong shortlists in 3 weeks.',
    appliesTo: ['sourcing'],
    appliedCount: 21,
    source: 'observed',
    relatedJobIds: [],
  },
  {
    id: 'mem-021',
    createdAt: '2026-03-24T15:00:00Z',
    category: 'scoring',
    attributedTo: TEAM.avery,
    trigger: 'When applying tenure heuristics to caregivers and career-break candidates',
    insight:
      'Avery flagged 3 strong returners I had auto-flagged. New rule: gaps >12 months for caregiving / health are explicitly NOT a deduction; instead, score reentry projects + skill currency. False-rejection on returners has dropped to 0% across the last cohort.',
    appliesTo: ['cv_screening'],
    appliedCount: 28,
    source: 'observed',
    relatedJobIds: [],
  },
  {
    id: 'mem-022',
    createdAt: '2026-03-22T11:08:33Z',
    category: 'pipeline',
    attributedTo: TEAM.maya,
    trigger: 'For Founding Designer roles at consumer-facing products',
    insight:
      '3 hires (Notion-style, Linear-style, our internal) shared one prompt that beat the portfolio walkthrough: "show me your most painful redesign and what you cut." Replaced portfolio review with this. Hires from the new loop scored 4.6 / 5 vs 3.8 / 5 from the old.',
    appliesTo: ['pipeline'],
    appliedCount: 18,
    source: 'pipeline_outcome',
    relatedJobIds: [],
  },
  {
    id: 'mem-023',
    createdAt: '2026-03-20T09:45:00Z',
    category: 'culture',
    attributedTo: TEAM.thomas,
    trigger: 'When evaluating "Day 1 trust" with founders at <Series A',
    insight:
      'Thomas scored 2 candidates 5→3 with note "would I let this person see the cap table?" — both failed his bar. Adjusted: founders\' final-round signal "would share cap table on day 1" is now a hard binary gate at <Series A, not a soft modifier.',
    appliesTo: ['ai_interview'],
    appliedCount: 24,
    source: 'score_edit',
    relatedJobIds: [],
  },
  {
    id: 'mem-024',
    createdAt: '2026-03-18T16:00:00Z',
    category: 'scoring',
    attributedTo: TEAM.maya,
    trigger: 'When evaluating CSMs without QBR experience but strong onboarding wins',
    insight:
      'Maya flagged 2 candidates I had dropped — they had no QBRs but ran 200+ user onboardings with NPS 60+. Splitting CSM rubric: "post-sale expansion" track ≠ "onboarding-led growth" track. Don\'t penalize the latter for missing the former\'s stage.',
    appliesTo: ['cv_screening', 'ai_interview'],
    appliedCount: 33,
    source: 'score_edit',
    relatedJobIds: [],
  },
  {
    id: 'mem-025',
    createdAt: '2026-03-15T10:00:00Z',
    category: 'sourcing',
    attributedTo: TEAM.devi,
    trigger: 'When evaluating ML Engineers from research-heavy backgrounds',
    insight:
      'Devi scored 1 candidate 4→2 noting "publishes a lot, ships nothing." Adjusting source-side filtering: GitHub commit cadence + production-shipping evidence (not just GitHub stars / paper count) is now a required signal for IC ML roles.',
    appliesTo: ['sourcing', 'cv_screening'],
    appliedCount: 31,
    source: 'score_edit',
    relatedJobIds: [],
  },
  {
    id: 'mem-026',
    createdAt: '2026-03-13T13:30:00Z',
    category: 'compensation',
    attributedTo: TEAM.kenji,
    trigger: 'When negotiating with senior candidates from public companies with vested RSUs',
    insight:
      'Kenji noticed 4 of last 5 declined offers had >$300K unvested RSU at current employer. Now standard practice: ask about RSU cliff timing during the screen, sweeten signing bonus to bridge ~12 months of vest. Closed 2 of 3 follow-up offers.',
    appliesTo: ['pipeline'],
    appliedCount: 27,
    source: 'pipeline_outcome',
    relatedJobIds: [],
  },
  {
    id: 'mem-027',
    createdAt: '2026-03-11T09:00:00Z',
    category: 'pipeline',
    attributedTo: TEAM.justin,
    trigger: 'For Head of Sales roles at Series A SaaS',
    insight:
      'Justin\'s pattern: 2 hires made via "ride-along on a real prospect call" outperformed 3 made via traditional case study. Mandate: Head of Sales finalists do a 30-min listen-only on a live deal before offer. The signal compresses 2 weeks of reference checks.',
    appliesTo: ['pipeline'],
    appliedCount: 36,
    source: 'pipeline_outcome',
    relatedJobIds: [],
  },
  {
    id: 'mem-028',
    createdAt: '2026-03-08T14:11:00Z',
    category: 'scoring',
    attributedTo: TEAM.sam,
    trigger: 'When evaluating frontend engineers for accessibility / a11y depth',
    insight:
      'Sam scored 2 candidates 3→4 citing strong WCAG / screen-reader testing experience. Adjusting: "shipped to an a11y-regulated market (gov, EDU, healthcare)" is +1 for Senior+ FE roles, not a niche specialization to be filtered out.',
    appliesTo: ['cv_screening', 'ai_interview'],
    appliedCount: 35,
    source: 'score_edit',
    relatedJobIds: [],
  },
  {
    id: 'mem-029',
    createdAt: '2026-03-05T11:00:00Z',
    category: 'scoring',
    attributedTo: TEAM.avery,
    trigger: 'When scoring junior candidates from non-CS backgrounds (bootcamp, self-taught)',
    insight:
      'Avery flagged my screen as biased against bootcamp grads. Recalibrated: pattern-matching on "shipped product + open-source contribution + on-call experience" is a stronger predictor than CS degree for L3-L4 IC roles. Removed the implicit CS-degree gate.',
    appliesTo: ['cv_screening'],
    appliedCount: 41,
    source: 'score_edit',
    relatedJobIds: [],
  },

  // ─── 60–120 days old (high applied counts) ──────────────────────────
  {
    id: 'mem-030',
    createdAt: '2026-03-02T09:30:00Z',
    category: 'culture',
    attributedTo: TEAM.maya,
    trigger: 'When detecting "build-fast vs build-right" axis mismatch on senior IC finalists',
    insight:
      'Maya called out 3 declines that traced to candidates wanting "more process / spec-driven" vs our ship-rough-iterate culture. Now explicitly probing: "how do you decide spec depth vs ship-and-learn?" in final round — answers correlate strongly with first-90-day outcomes.',
    appliesTo: ['ai_interview'],
    appliedCount: 47,
    source: 'pipeline_outcome',
    relatedJobIds: [],
  },
  {
    id: 'mem-031',
    createdAt: '2026-02-26T15:20:00Z',
    category: 'sourcing',
    attributedTo: TEAM.thomas,
    trigger: 'When sourcing co-founders or first-engineer-style ICs',
    insight:
      'Thomas added: "alumni from Replit, Linear, Vercel, Anthropic, and Cursor" as a +2 sourcing prior. Reasoning: shared design taste + comfort with AI-native tooling. 2 of our 3 first-engineer hires came from this pool — extending to designer #1 sourcing as well.',
    appliesTo: ['sourcing'],
    appliedCount: 53,
    source: 'manual',
    relatedJobIds: [],
  },
  {
    id: 'mem-032',
    createdAt: '2026-02-22T10:45:00Z',
    category: 'scoring',
    attributedTo: TEAM.kenji,
    trigger: 'When evaluating Senior PM candidates without explicit growth metrics',
    insight:
      'Kenji scored 2 candidates 4→3 — both led products without clear north-star ownership. New rule: require 1+ named NSM moved by ≥20% in last role for any Senior+ PM role, or surfaced narrative for why the org didn\'t track one.',
    appliesTo: ['cv_screening', 'ai_interview'],
    appliedCount: 62,
    source: 'score_edit',
    relatedJobIds: [],
  },
  {
    id: 'mem-033',
    createdAt: '2026-02-18T13:00:00Z',
    category: 'compensation',
    attributedTo: TEAM.avery,
    trigger: 'When extending equity to first 5 Eng hires at <Series A',
    insight:
      'Pattern from 4 accepts vs 3 declines: equity offers framed as % of cap-table were accepted; offers framed as $ value were declined. Switched default offer language to ".X% of total cap, refresh at Series A." Accept rate moved from 57% → 89% over the next 6 offers.',
    appliesTo: ['pipeline'],
    appliedCount: 58,
    source: 'pipeline_outcome',
    relatedJobIds: [],
  },
  {
    id: 'mem-034',
    createdAt: '2026-02-12T11:30:00Z',
    category: 'pipeline',
    attributedTo: TEAM.devi,
    trigger: 'For Senior Eng pipelines at Series A SaaS with sub-30 candidate volume',
    insight:
      'With <30 candidates per pipeline, the recruiter screen + take-home stage burns calendar without adding signal. Compressed to: recruiter intro → live coding + system design (same 90-min block) → offer. Time-to-offer cut by 11 days, accept rate unchanged.',
    appliesTo: ['pipeline'],
    appliedCount: 71,
    source: 'pipeline_outcome',
    relatedJobIds: [],
  },
  {
    id: 'mem-035',
    createdAt: '2026-02-08T09:00:00Z',
    category: 'scoring',
    attributedTo: TEAM.justin,
    trigger: 'When scoring AE candidates against industry-vertical knowledge (fintech, health, govtech)',
    insight:
      'Justin observed: 3 AE hires for fintech struggled without "regulated industries" context (KYC, SOC2, banking-as-a-platform). Adjusting AE rubric per vertical: fintech / health / govtech now require explicit prior-vertical experience, not just general SaaS sales.',
    appliesTo: ['cv_screening', 'ai_interview'],
    appliedCount: 79,
    source: 'observed',
    relatedJobIds: [],
  },
  {
    id: 'mem-036',
    createdAt: '2026-02-04T14:30:00Z',
    category: 'culture',
    attributedTo: TEAM.sam,
    trigger: 'When evaluating senior IC candidates for "willingness to mentor"',
    insight:
      'Sam added explicit signal: "mentored 2+ junior engineers + ran 1+ internal tech talk" as required for Senior+ roles, not just Staff. Reasoning: at <30-eng, every senior IC is implicitly part-time tech lead — non-negotiable, even when not titled as such.',
    appliesTo: ['cv_screening', 'ai_interview'],
    appliedCount: 84,
    source: 'manual',
    relatedJobIds: [],
  },
  {
    id: 'mem-037',
    createdAt: '2026-01-28T10:15:00Z',
    category: 'sourcing',
    attributedTo: TEAM.maya,
    trigger: 'When sourcing for Designer roles via portfolio platforms',
    insight:
      'Maya tracked: Dribbble / Behance hits skewed to consumer; Read.cv + Are.na yielded 4 of 6 strong B2B Designer candidates. Reweighted: B2B / SaaS Designer sources — Read.cv +20%, Are.na +15%, Dribbble +0%. Old weights were inherited from a consumer playbook.',
    appliesTo: ['sourcing'],
    appliedCount: 96,
    source: 'observed',
    relatedJobIds: [],
  },

  // ─── 120–180 days old (very high applied counts) ────────────────────
  {
    id: 'mem-038',
    createdAt: '2026-01-20T13:00:00Z',
    category: 'scoring',
    attributedTo: TEAM.thomas,
    trigger: 'When evaluating GTM hires before $1M ARR',
    insight:
      'Thomas\'s pattern after 3 successful and 2 failed pre-$1M GTM hires: required signal is "has founded or quasi-founded a sales motion (built playbook, hired SDRs)" — not "managed N reps at scale." Recalibrated GTM rubric for pre-$1M ARR roles. Two hires since.',
    appliesTo: ['cv_screening', 'ai_interview'],
    appliedCount: 117,
    source: 'pipeline_outcome',
    relatedJobIds: [],
  },
  {
    id: 'mem-039',
    createdAt: '2026-01-10T15:30:00Z',
    category: 'compensation',
    attributedTo: TEAM.justin,
    trigger: 'When negotiating senior-level comp for early-Series-A start-ups',
    insight:
      'Justin\'s pattern: 4 of 5 declines centered on equity, not base. Adjusting close strategy: lead with equity uplift (refresh at next round) rather than base bumps for senior roles at Series A. Closed 2 of 3 follow-up offers using the new framing.',
    appliesTo: ['pipeline'],
    appliedCount: 134,
    source: 'score_edit',
    relatedJobIds: [],
  },
  {
    id: 'mem-040',
    createdAt: '2025-12-15T10:00:00Z',
    category: 'scoring',
    attributedTo: TEAM.sam,
    trigger: 'When evaluating Backend candidates for "owns production" depth',
    insight:
      'Sam scored 3 candidates down for "no on-call rotation experience." Pattern checks out: junior+ candidates who haven\'t handled a 2am-page tend to under-architect for failure. Recalibrated: on-call ownership = required for L4+ Backend, +1 modifier even at L3.',
    appliesTo: ['cv_screening', 'ai_interview'],
    appliedCount: 287,
    source: 'score_edit',
    relatedJobIds: [],
  },
];
